import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Asset, Obligation, Reminder, Project } from './types';
import * as db from './lib/db';

interface AppData {
  assets: Asset[];
  obligations: Obligation[];
  remindersByObligation: Record<string, Reminder[]>;
  projects: Project[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getAsset: (id: string | null) => Asset | undefined;
}

const AppDataContext = createContext<AppData | null>(null);

export function AppDataProvider({ children }: { children: React.ReactNode }) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [remindersByObligation, setRemindersByObligation] = useState<Record<string, Reminder[]>>(
    {}
  );
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, o, r, p] = await Promise.all([
        db.listAssets(),
        db.listObligations(),
        db.listAllReminders(),
        db.listProjects(),
      ]);
      setAssets(a);
      setObligations(o);
      setRemindersByObligation(r);
      setProjects(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getAsset = useCallback(
    (id: string | null) => (id ? assets.find((a) => a.id === id) : undefined),
    [assets]
  );

  const value = useMemo(
    () => ({
      assets,
      obligations,
      remindersByObligation,
      projects,
      loading,
      error,
      refresh,
      getAsset,
    }),
    [assets, obligations, remindersByObligation, projects, loading, error, refresh, getAsset]
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used inside AppDataProvider');
  return ctx;
}
